import { AfterViewInit, Component, effect, signal, ViewChild } from '@angular/core';
import { AuthService } from 'app/core/auth/auth.service';
import { ApexOptions } from 'apexcharts';
import { Router } from '@angular/router';
import { TranslocoModule, TranslocoPipe } from '@jsverse/transloco';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatRippleModule } from '@angular/material/core';
import { MatMenuModule } from '@angular/material/menu';
import { MatTabsModule } from '@angular/material/tabs';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { NgApexchartsModule } from 'ng-apexcharts';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatDividerModule } from '@angular/material/divider';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { CommonModule, CurrencyPipe } from '@angular/common';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { LegacyContractStatusEnum, SalesAdminDashboardContractsDto, SalesAdminDashboardService, SalesAdminDashboardSummaryResponse } from './sales-admin-dashboard.service';
import { AlertService } from 'app/core/services/alert.service';

@Component({
  selector: 'app-sales-admin-dashboard',
  templateUrl: './sales-admin-dashboard.component.html',
  styleUrls: ['./sales-admin-dashboard.component.css'],
  imports: [
    TranslocoModule,
    MatIconModule,
    MatButtonModule,
    MatRippleModule,
    MatMenuModule,
    MatTabsModule,
    MatButtonToggleModule,
    NgApexchartsModule,
    MatTableModule,
    MatButtonModule,
    MatDividerModule,
    MatSortModule,
    MatProgressBarModule,
    CurrencyPipe,
    CommonModule,
    TranslocoPipe
  ]
})
export class SalesAdminDashboardComponent implements AfterViewInit {
  @ViewChild('recentTransactionsTable', { read: MatSort })
  recentTransactionsTableMatSort: MatSort;
  chartTopSellers = signal<ApexOptions>({});

  data = signal<SalesAdminDashboardSummaryResponse>(null);
  accountBalanceOptions: ApexOptions;
  topSellersDataSource: MatTableDataSource<any> =
    new MatTableDataSource();


  recentTransactionsTableColumns: string[] = ['sellerName', 'amount', 'total'];
  now = new Date();
  availableYears = [this.now.getFullYear(), this.now.getFullYear() - 1];

  soldCarsTimePeriod = signal<string>('Last 7 Days');
  setYear = signal<number>(this.now.getFullYear());
  salesPeriod = signal<string>('30 days');
  selectedTabIndex: number = 0;
  defaultColors = [
    '#319795', '#4FD1C5', '#ED8936', '#ECC94B', '#805AD5', '#CBD5E0', '#F56565', '#68D391', '#63B3ED'
  ];

  get soldCarsPeriod() {
    switch (this.soldCarsTimePeriod()) {
      case 'Last 7 Days':
        return "Last7Days";
      case 'Last 30 Days':
        return "Last30Days";
      case 'Last 3 Months':
        return "Last3Months";
      case 'Last 6 Months':
        return "Last6Months";
      case 'Last 9 Months':
        return "Last9Months";
      case 'Last Year':
        return "LastYear";
      default:
        return "Last30Days";
    }
  }

  get salesTime() {
    switch (this.salesPeriod()) {
      case 'Last 7 Days':
        return "Last7Days";
      case 'Last 30 Days':
        return "Last30Days";
      case 'Last 3 Months':
        return "Last3Months";
      case 'Last 6 Months':
        return "Last6Months";
      case 'Last 9 Months':
        return "Last9Months";
      case 'Last Year':
        return "LastYear";
      default:
        return "Last30Days";
    }
  }

  constructor(
    public authService: AuthService,
    private _salesDashboardService: SalesAdminDashboardService,
    private _router: Router,
    private alert: AlertService
  ) {
    effect(() => {
      console.log("parameter changed")
      this.getDashboardData();
    });
  }

  ngAfterViewInit(): void {
    // Make the data source sortable
    this.topSellersDataSource.sort =
      this.recentTransactionsTableMatSort;
  }

  getDashboardData() {
    this._salesDashboardService.getSalesAdminDashboardData(this.soldCarsTimePeriod(), this.setYear(), this.salesPeriod()).subscribe({
      next: (response: any) => {
        this.data.set(response.value);
        this.topSellersDataSource.data =
          response.value.topSellers;

        setTimeout(() => this._prepareChartData(), 300);
      },
      error: (error) => {
        this.alert.errorFromHttp(error);
      },
    });

    window['Apex'] = {
      chart: {
        events: {
          mounted: (chart: any, options?: any): void => {
            this._fixSvgFill(chart.el);
          },
          updated: (chart: any, options?: any): void => {
            this._fixSvgFill(chart.el);
          },
        },
      },
    };
  }

  trackByFn(index: number, item: any): any {
    return item.id || index;
  }

  private _fixSvgFill(element: Element): void {
    const currentURL = this._router.url;
    Array.from(element.querySelectorAll('*[fill]'))
      .filter((el) => el.getAttribute('fill').indexOf('url(') !== -1)
      .forEach((el) => {
        const attrVal = el.getAttribute('fill');
        el.setAttribute(
          'fill',
          `url(${currentURL}${attrVal.slice(attrVal.indexOf('#'))}`
        );
      });
  }

  private _prepareChartData() {
    const seriesData: { x: Date; y: number }[] =
      this.getCompletedContractsPerWeek(
        this.data().totalContracts,
        this.setYear()
      );

    var labelsTopSellers = [];
    var seriesTopSellers = [];
    let topSellerTotal = 0;

    for (const seller of this.data().topSellers) {
      const percent = this.data().totalSales > 0
        ? +(seller.amountSold / this.data().totalSales * 100).toFixed(2)
        : 0;

      labelsTopSellers.push(seller.sellerName);
      seriesTopSellers.push(percent);
      topSellerTotal += percent;
    }

    const otherPercent = +(100 - topSellerTotal).toFixed(2);
    if (otherPercent > 0) {
      labelsTopSellers.push('Other');
      seriesTopSellers.push(otherPercent);
    }
    this.accountBalanceOptions = {
      chart: {
        animations: {
          speed: 400,
          animateGradually: {
            enabled: false,
          },
        },
        fontFamily: 'inherit',
        foreColor: 'inherit',
        width: '100%',
        height: '100%',
        type: 'area',
        sparkline: {
          enabled: true,
        },
      },
      colors: ['#A3BFFA', '#667EEA'],
      fill: {
        colors: ['#CED9FB', '#AECDFD'],
        opacity: 0.5,
        type: 'solid',
      },
      series: [
        {
          name: 'Completed Contracts',
          data: seriesData,
        },
      ],
      stroke: {
        curve: 'straight',
        width: 2,
      },
      tooltip: {
        followCursor: true,
        theme: 'dark',
        x: {
          format: 'MMM dd, yyyy',
        },
        y: {
          formatter: (value): string => value.toString(),
        },
      },
      xaxis: {
        type: 'datetime',
      },
    };
    this.chartTopSellers.set({
      chart: {
        animations: {
          speed: 400,
          animateGradually: {
            enabled: false,
          },
        },
        fontFamily: 'inherit',
        foreColor: 'inherit',
        height: '100%',
        type: 'donut',
        sparkline: {
          enabled: true,
        },
      },
      colors: this.defaultColors.slice(0, seriesTopSellers.length),
      labels: labelsTopSellers,
      plotOptions: {
        pie: {
          customScale: 0.9,
          expandOnClick: false,
          donut: {
            size: '70%',
          },
        },
      },
      series: seriesTopSellers,
      states: {
        hover: {
          filter: {
            type: 'none',
          },
        },
        active: {
          filter: {
            type: 'none',
          },
        },
      },
      tooltip: {
        enabled: true,
        fillSeriesColor: false,
        theme: 'dark',
        custom: ({
          seriesIndex,
          w,
        }): string => `<div class="flex items-center h-8 min-h-8 max-h-8 px-3">
                                                     <div class="w-3 h-3 rounded-full" style="background-color: ${w.config.colors[seriesIndex]};"></div>
                                                     <div class="ml-2 text-md leading-none">${w.config.labels[seriesIndex]}:</div>
                                                     <div class="ml-2 text-md font-bold leading-none">${w.config.series[seriesIndex]}%</div>
                                                 </div>`,
      },
    });
  }


  getCompletedContractsPerWeek(
    contracts: SalesAdminDashboardContractsDto[],
    year: number
  ) {
    const result: { x: Date; y: number }[] = [];
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentWeek = this.getWeekNumber(now);

    const maxWeeks = year === currentYear ? currentWeek : 52;

    // Initialize buckets for each week
    const weeklyBuckets = new Array(maxWeeks).fill(0);

    for (const contract of contracts) {
      if (contract.status !== LegacyContractStatusEnum.Completed) continue;

      const contractDate = new Date(contract.date);
      if (contractDate.getFullYear() !== year) continue;

      const week = this.getWeekNumber(contractDate);
      if (week >= 1 && week <= maxWeeks) {
        weeklyBuckets[week - 1]++; // index = week - 1
      }
    }

    // Convert week buckets into x/y chart data points
    for (let i = 0; i < maxWeeks; i++) {
      const weekStart = this.getStartOfWeek(year, i + 1);
      result.push({
        x: weekStart,
        y: weeklyBuckets[i]
      });
    }

    return result;
  }
  private getWeekNumber(date: Date): number {
    const temp = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = temp.getUTCDay() || 7;
    temp.setUTCDate(temp.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(temp.getUTCFullYear(), 0, 1));
    const weekNo = Math.ceil(((temp.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
    return weekNo;
  }

  private getStartOfWeek(year: number, week: number): Date {
    const jan1 = new Date(year, 0, 1);
    const jan1Day = jan1.getDay() || 7;
    const daysOffset = (week - 1) * 7 - (jan1Day - 1);
    const weekStart = new Date(year, 0, 1 + daysOffset);
    weekStart.setHours(0, 0, 0, 0);
    return weekStart;
  }

  get totalSalePriceCompleted(): number {
    return this.data()?.totalContracts
      .filter(x => x.status == LegacyContractStatusEnum.Completed)
      .reduce((sum, x) => sum + x.totalSalePrice, 0) ?? 0;
  }

  get totalSalePriceInProgress(): number {
    return this.data()?.totalContracts
      .filter(x => x.status !== LegacyContractStatusEnum.Completed)
      .reduce((sum, x) => sum + x.totalSalePrice, 0) ?? 0;
  }

  get totalPurchasePriceCompleted(): number {
    return this.data()?.totalContracts
      .filter(x => x.status == LegacyContractStatusEnum.Completed)
      .reduce((sum, x) => sum + x.totalSalePrice, 0) ?? 0;
  }

  get totalPurchasePriceInProgress(): number {
    return this.data()?.totalContracts
      .filter(x => x.status !== LegacyContractStatusEnum.Completed)
      .reduce((sum, x) => sum + x.totalSalePrice, 0) ?? 0;
  }


  get getReservedVehiclesCount(): number {
    return this.data()?.reservedVehicles?.length ?? 0;
  }
  get getContractsWithNoDocs(): number {
    return this.data()?.contractsInProgress.filter(x => x.awaitingDocs).length ?? 0;
  }
  get getContractsInProgressCount(): number {
    return this.data()?.contractsInProgress?.length ?? 0;
  }
  get getSoldCarsCount(): number {
    return this.data()?.soldCarsThisWeek?.length ?? 0;
  }
  get getDeliveredCarsCount(): number {
    return this.data()?.soldCarsThisWeek.filter(x => x.delivered).length ?? 0;
  }
  get getPendingDeliveriesCount(): number {
    return this.data()?.pendingDeliveries ?? 0;
  }
  get getCompletedContracts(): number {
    return this.data()?.totalContracts.filter(x => x.status == LegacyContractStatusEnum.Completed).length ?? 0;
  }

  get totalCompletedPaid(): number {
    return this.data()?.totalContracts
      .filter(x => x.status === LegacyContractStatusEnum.Completed)
      .reduce((sum, x) => sum + x.totalPaid, 0) ?? 0;
  }

  get totalInProgressPaid(): number {
    return this.data()?.totalContracts
      .filter(x => x.status !== LegacyContractStatusEnum.Completed)
      .reduce((sum, x) => sum + x.totalPaid, 0) ?? 0;
  }

}
